import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  FaSearch,
  FaRegHeart,
  FaClinicMedical,
  FaUserMd,
  FaInfoCircle,
  FaTimes,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import defaultDoctorImage from "../../assets/images/doctors/doc.webp";
import ConfirmationPopup from "./ConfirmationPopup";

interface IllnessModal {
  id: number;
  illness: string;
}

interface Speciality {
  id: number;
  speciality: string;
}

interface Illness {
  id: number;
}

interface DoctorTableModal {
  id: number;
  name: string;
  description: string;
  degree: string;
  gender: string;
  languages: string;
  experience: string;
  img: string;
  status: number;
  specialities: Speciality[];
  illnesses: Illness[];
}

const DoctorIllnessSelector: React.FC<{
  illnesses: IllnessModal[];
  doctors: DoctorTableModal[];
  doctorTableLoading: boolean;
  illnessLoading: boolean;
}> = ({ illnesses, doctors, doctorTableLoading, illnessLoading }) => {
  // State management
  const [activeTab, setActiveTab] = useState<"illnesses" | "doctors">(
    "illnesses"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIllness, setSelectedIllness] = useState<IllnessModal | null>(
    null
  );
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorTableModal | null>(
    null
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filtered data
  const filteredIllnesses = useMemo(() => {
    return illnesses.filter((illness) =>
      illness.illness.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [illnesses, searchTerm]);

  const filteredDoctors = useMemo(() => {
    return doctors.filter((doctor) =>
      doctor.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [doctors, searchTerm]);

  // Handlers
  const handleIllnessSelect = (illness: IllnessModal) => {
    setSelectedIllness(illness);
    setActiveTab("doctors");
    setSearchTerm("");
    containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDoctorSelect = (doctor: DoctorTableModal) => {
    setSelectedDoctor(doctor);
    setActiveTab("illnesses");
    setSearchTerm("");
    containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleResetSelection = () => {
    setSelectedIllness(null);
    setSelectedDoctor(null);
    setSearchTerm("");
  };

  // Check if both are selected to show confirmation
  useEffect(() => {
    if (selectedIllness && selectedDoctor) {
      setShowConfirmation(true);
    }
  }, [selectedIllness, selectedDoctor]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.3,
      },
    },
  };

  const cardHoverVariants = {
    hover: { y: -5, boxShadow: "0 10px 20px rgba(0,0,0,0.1)" },
    tap: { scale: 0.98 },
  };

  const loadingItemVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 0.5,
    },
  };

  // Loading skeleton items
 const renderLoadingSkeleton = (count: number, type: "illness" | "doctor") => {
  return Array.from({ length: count }).map((_, index) => (
    <motion.div
      key={`loading-${type}-${index}`}
      variants={loadingItemVariants}
      initial="hidden"
      animate="visible"
      transition={{
        repeat: Infinity,
        repeatType: "reverse",
        duration: 1.5,
        ease: "easeInOut"
      }}
      className="h-full"
    >
      <div
        className={`p-3 md:p-4 rounded-lg border h-full ${
          type === "illness" ? "min-h-[80px]" : "min-h-[160px]"
        } bg-gray-50 border-gray-200`}
      >
        {type === "doctor" && (
          <div className="flex items-start space-x-3">
            {/* Doctor avatar skeleton with pulse animation */}
            <div className="relative overflow-hidden w-10 h-10 md:w-12 md:h-12 rounded-full bg-gray-200">
              <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer" />
            </div>
            <div className="flex-1 space-y-3">
              {/* Name */}
              <div className="h-5 w-3/4 bg-gray-200 rounded-md relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer" />
              </div>
              {/* Degree */}
              <div className="h-4 w-1/2 bg-gray-200 rounded-md relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer" />
              </div>
              {/* Specialties */}
              <div className="flex gap-2">
                <div className="h-5 w-16 bg-gray-200 rounded-md relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer" />
                </div>
                <div className="h-5 w-16 bg-gray-200 rounded-md relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer" />
                </div>
              </div>
            </div>
          </div>
        )}
        {type === "illness" && (
          <div className="space-y-3">
            <div className="flex justify-between">
              {/* Illness name */}
              <div className="h-5 w-3/4 bg-gray-200 rounded-md relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer" />
              </div>
              {/* Favorite icon */}
              <div className="h-5 w-5 bg-gray-200 rounded-full relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer" />
              </div>
            </div>
            {/* Doctor count */}
            <div className="h-4 w-1/2 bg-gray-200 rounded-md relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer" />
            </div>
          </div>
        )}
      </div>
    </motion.div>
  ));
};
  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 bg-white rounded-xl shadow-lg">
      {/* Header with progress indicator */}
      <div className="flex flex-col mb-4 md:mb-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">
            Find Your Specialist
          </h2>
          <button
            onClick={() => setShowGuidelines(!showGuidelines)}
            className="text-blue-500 hover:text-blue-700 transition-colors"
          >
            <FaInfoCircle className="text-lg" />
          </button>
        </div>
        
        {/* Progress steps */}
        <div className="flex items-center mt-4 mb-2">
          <div className={`flex items-center ${selectedIllness ? 'text-green-600' : 'text-gray-400'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${selectedIllness ? 'bg-green-100' : 'bg-gray-100'}`}>
              {selectedIllness ? '✓' : '1'}
            </div>
            <div className={`ml-2 text-sm ${selectedIllness ? 'font-medium' : ''}`}>Select Condition</div>
          </div>
          
          <div className={`flex-1 h-1 mx-2 ${selectedIllness ? 'bg-green-100' : 'bg-gray-100'}`}></div>
          
          <div className={`flex items-center ${selectedDoctor ? 'text-green-600' : 'text-gray-400'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${selectedDoctor ? 'bg-green-100' : 'bg-gray-100'}`}>
              {selectedDoctor ? '✓' : '2'}
            </div>
            <div className={`ml-2 text-sm ${selectedDoctor ? 'font-medium' : ''}`}>Choose Specialist</div>
          </div>
        </div>
      </div>

      {/* Guidelines Section - Collapsible */}
      <div className="mb-4 md:mb-6 bg-gradient-to-r from-green-50 to-blue-50 p-3 md:p-4 rounded-lg border border-gray-200">
        <details className="group">
          <summary className="flex items-center cursor-pointer list-none">
            <FaInfoCircle className="text-blue-500 text-lg mt-0.5 mr-3 flex-shrink-0" />
            <h3 className="font-medium text-gray-800">How to find your specialist</h3>
            <span className="ml-auto transition-transform group-open:rotate-180">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </summary>
          <ul className="mt-3 text-sm text-gray-600 space-y-2 pl-7">
            <li className="flex items-start">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 mr-2"></span>
              <span>Start by selecting your health condition or searching for a specific illness</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2"></span>
              <span>Browse doctors who specialize in treating your selected condition</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 mr-2"></span>
              <span>You can also search directly for doctors by name or specialty</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-yellow-500 mt-1.5 mr-2"></span>
              <span>Once you've selected both, you'll proceed to schedule your appointment</span>
            </li>
          </ul>
        </details>
      </div>

      {/* Selected Items Preview - Sticky */}
      {(selectedIllness || selectedDoctor) && (
        <div className="sticky top-0 z-20 mb-4 md:mb-6 p-3 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              {selectedIllness && (
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center">
                  {selectedIllness.illness}
                  <button
                    onClick={() => setSelectedIllness(null)}
                    className="ml-2 text-green-600 hover:text-green-800"
                  >
                    <FaTimes size={12} />
                  </button>
                </span>
              )}
              {selectedDoctor && (
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center">
                  {selectedDoctor.name}
                  <button
                    onClick={() => setSelectedDoctor(null)}
                    className="ml-2 text-green-600 hover:text-green-800"
                  >
                    <FaTimes size={12} />
                  </button>
                </span>
              )}
            </div>
            <button
              onClick={handleResetSelection}
              className="text-xs font-medium text-red-600 hover:text-red-800 flex items-center"
            >
              <span>Reset all</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div 
        ref={containerRef}
        className="space-y-4"
        style={{
          maxHeight: "calc(100vh - 220px)",
          overflowY: "auto",
        }}
      >
        {/* Sticky Search Bar */}
        <div className="sticky top-0 z-10 bg-white pt-2 pb-2">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={
                activeTab === "illnesses"
                  ? "Search conditions..."
                  : "Search specialists..."
              }
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <FaTimes size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === "illnesses" ? (
            <motion.div
              key="illness-view"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {illnessLoading ? (
                <motion.div
                  className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {renderLoadingSkeleton(6, "illness")}
                </motion.div>
              ) : (
                <motion.div
                  className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {filteredIllnesses
                    .filter((ill) =>
                      selectedDoctor
                        ? selectedDoctor.illnesses.some(
                            (docIll) => docIll.id === ill.id
                          )
                        : true
                    )
                    .map((illness) => (
                      <motion.div
                        key={illness.id}
                        variants={itemVariants}
                        whileHover="hover"
                        whileTap="tap"
                        className="h-full"
                      >
                        <motion.div
                          className={`p-3 rounded-lg border cursor-pointer transition-all h-full flex flex-col ${
                            selectedIllness?.id === illness.id
                              ? "bg-green-50 border-green-300"
                              : "bg-white border-gray-200 hover:border-green-200"
                          }`}
                          variants={cardHoverVariants}
                          onClick={() => handleIllnessSelect(illness)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex items-center min-w-0">
                              <FaClinicMedical className="text-green-500 mr-2 text-lg flex-shrink-0" />
                              <h3
                                className="font-medium text-gray-800 truncate text-sm"
                                title={illness.illness}
                              >
                                {illness.illness}
                              </h3>
                            </div>
                            <button className="text-green-400 hover:text-green-600 transition-colors flex-shrink-0 ml-2">
                              <FaRegHeart className="text-sm" />
                            </button>
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            {
                              doctors.filter((d) =>
                                d.illnesses.some((i) => i.id === illness.id)
                              ).length
                            }{" "}
                            specialists available
                          </div>
                        </motion.div>
                      </motion.div>
                    ))}
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="doctor-view"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {doctorTableLoading ? (
                <motion.div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {renderLoadingSkeleton(4, "doctor")}
                </motion.div>
              ) : (
                <motion.div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {filteredDoctors
                    .filter((doc) =>
                      selectedIllness
                        ? doc.illnesses.some(
                            (docIll) => docIll.id === selectedIllness.id
                          )
                        : true
                    )
                    .map((doctor) => (
                      <motion.div
                        key={doctor.id}
                        variants={itemVariants}
                        whileHover="hover"
                        whileTap="tap"
                        className="h-full"
                      >
                        <motion.div
                          className={`p-3 rounded-lg border cursor-pointer transition-all h-full flex flex-col ${
                            selectedDoctor?.id === doctor.id
                              ? "bg-green-50 border-green-300"
                              : "bg-white border-gray-200 hover:border-green-200"
                          }`}
                          variants={cardHoverVariants}
                          onClick={() => handleDoctorSelect(doctor)}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <img
                                src={doctor.img || defaultDoctorImage}
                                alt={doctor.name}
                                className="w-10 h-10 rounded-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src =
                                    defaultDoctorImage;
                                }}
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex justify-between items-start">
                                <h3 className="font-semibold text-gray-800 truncate text-sm">
                                  {doctor.name}
                                </h3>
                                <button className="text-green-500 hover:text-green-700">
                                  <FaRegHeart />
                                </button>
                              </div>
                              <p className="text-xs text-gray-600 truncate">
                                {doctor.degree}
                              </p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {doctor.specialities.slice(0, 2).map((spec) => (
                                  <span
                                    key={spec.id}
                                    className="inline-block bg-green-100 text-green-800 text-xs px-1.5 py-0.5 rounded"
                                  >
                                    {spec.speciality}
                                  </span>
                                ))}
                                {doctor.specialities.length > 2 && (
                                  <span className="inline-block bg-gray-100 text-gray-800 text-xs px-1.5 py-0.5 rounded">
                                    +{doctor.specialities.length - 2}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <p className="text-xs text-gray-500">Treats:</p>
                            <p className="text-xs text-gray-700 line-clamp-2">
                              {illnesses
                                .filter((i) =>
                                  doctor.illnesses.some((di) => di.id === i.id)
                                )
                                .map((i) => i.illness)
                                .join(", ")}
                            </p>
                          </div>
                        </motion.div>
                      </motion.div>
                    ))}
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty State */}
        {!illnessLoading && activeTab === "illnesses" && filteredIllnesses.length === 0 && (
          <motion.div
            className="text-center py-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p className="text-gray-500">No conditions found</p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="mt-2 text-green-600 hover:text-green-800 text-sm"
              >
                Clear search
              </button>
            )}
          </motion.div>
        )}

        {!doctorTableLoading && activeTab === "doctors" && filteredDoctors.length === 0 && (
          <motion.div
            className="text-center py-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p className="text-gray-500">No specialists found</p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="mt-2 text-green-600 hover:text-green-800 text-sm"
              >
                Clear search
              </button>
            )}
          </motion.div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="sticky bottom-0 bg-white pt-4 pb-2 border-t border-gray-200">
        <div className="flex justify-between">
          <button
            onClick={() => setActiveTab("illnesses")}
            className={`flex-1 py-2 px-4 rounded-l-lg flex items-center justify-center transition-all ${
              activeTab === "illnesses"
                ? "bg-green-100 text-green-700 font-medium"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            <FaClinicMedical className="mr-2" />
            <span>Conditions</span>
          </button>
          <button
            onClick={() => setActiveTab("doctors")}
            className={`flex-1 py-2 px-4 rounded-r-lg flex items-center justify-center transition-all ${
              activeTab === "doctors"
                ? "bg-green-100 text-green-700 font-medium"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            <FaUserMd className="mr-2" />
            <span>Specialists</span>
          </button>
        </div>
      </div>

      {/* Confirmation Popup */}
      {showConfirmation && selectedIllness && selectedDoctor && (
        <ConfirmationPopup
          illness={selectedIllness}
          doctor={selectedDoctor}
          onClose={() => {
            setShowConfirmation(false);
            setSelectedDoctor(null);
            setSelectedIllness(null);
          }}
        />
      )}
    </div>
  );
};

export default DoctorIllnessSelector;